/**
 * Track-based distance utilities for the StationView "In Progress" tab (DEV-2587).
 *
 * SemanticMap.track is a GeoJSON FeatureCollection whose features are LineStrings
 * or MultiLineStrings with stringified coordinates. We normalize that into a
 * `PreparedTrack` (one-time work per semantic map) and then expose two operations:
 *
 *   - `snapPoint(prepared, lat, lng)` — find the closest point on any line
 *   - `arcLengthBetween(prepared, snapA, snapB)` — distance along the track
 *
 * Both Turf.js (`@turf/nearest-point-on-line`, `@turf/length`) do the heavy lifting.
 *
 * The prepared track is cached in localStorage keyed by semanticMapId + a version
 * stamp so future schema changes invalidate it. Quota errors fall through to
 * in-memory only — caching is a performance optimization, not a correctness
 * requirement.
 */

import { length as turfLength } from "@turf/length";
import { nearestPointOnLine } from "@turf/nearest-point-on-line";
import type { Feature, LineString, Position } from "geojson";

// Bump this when PreparedTrack's serialized shape changes.
const PREPARED_TRACK_CACHE_VERSION = 1;
const CACHE_KEY_PREFIX = "fleet-app:prepared-track:v";
const KILOMETERS_TO_METERS = 1000;
// Snap is considered a miss if the perpendicular distance from the input point
// to the nearest line exceeds this.
const SNAP_PERP_THRESHOLD_METERS = 2;

export interface PreparedLine {
  /** Per-segment cumulative distance from the line's start, in meters.
   *  Length === coords.length; cumulative[0] === 0; cumulative[N-1] === totalLengthMeters. */
  cumulative: number[];
  /** Coordinates as [lng, lat] pairs — already coerced to numbers. */
  coords: Position[];
  /** Bounding box [minLng, minLat, maxLng, maxLat] of this line. */
  bbox: [number, number, number, number];
  /** Total length of this line in meters. */
  totalLengthMeters: number;
}

export interface PreparedTrack {
  semanticMapId: string;
  version: number;
  lines: PreparedLine[];
}

export interface SnapResult {
  /** Index into prepared.lines. */
  lineIdx: number;
  /** Index of the segment within that line (0-based; segment N is between coords[N] and coords[N+1]). */
  segIdx: number;
  /** Parametric position along the segment (0..1). */
  t: number;
  /** Distance along the line from its start, in meters. */
  distAlongLine: number;
  /** Perpendicular distance from the input point to the snapped point, in meters. */
  perpDistMeters: number;
}

const isFiniteNumber = (n: unknown): n is number =>
  typeof n === "number" && Number.isFinite(n);

const coerceCoord = (raw: unknown): Position | null => {
  if (!Array.isArray(raw) || raw.length < 2) return null;
  const lng = typeof raw[0] === "string" ? Number(raw[0]) : raw[0];
  const lat = typeof raw[1] === "string" ? Number(raw[1]) : raw[1];
  if (!isFiniteNumber(lng) || !isFiniteNumber(lat)) return null;
  return [lng, lat];
};

const computeBbox = (coords: Position[]): [number, number, number, number] => {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }
  return [minLng, minLat, maxLng, maxLat];
};

const buildLine = (coords: Position[]): PreparedLine | null => {
  if (coords.length < 2) return null;
  const lineFeature: Feature<LineString> = {
    type: "Feature",
    geometry: { type: "LineString", coordinates: coords },
    properties: {},
  };
  const cumulative = new Array<number>(coords.length);
  cumulative[0] = 0;
  for (let i = 1; i < coords.length; i++) {
    const segFeature: Feature<LineString> = {
      type: "Feature",
      geometry: {
        type: "LineString",
        coordinates: [coords[i - 1], coords[i]],
      },
      properties: {},
    };
    const segMeters = turfLength(segFeature) * KILOMETERS_TO_METERS;
    cumulative[i] = cumulative[i - 1] + segMeters;
  }
  const totalLengthMeters =
    turfLength(lineFeature) * KILOMETERS_TO_METERS ||
    cumulative[cumulative.length - 1];
  return {
    cumulative,
    coords,
    bbox: computeBbox(coords),
    totalLengthMeters,
  };
};

const explodeFeatureToLines = (feature: SemanticMapFeature): Position[][] => {
  if (!feature?.geometry) return [];
  const { type, coordinates } = feature.geometry;
  if (type === "LineString") {
    const coords = (coordinates as unknown[])
      .map(coerceCoord)
      .filter((c): c is Position => c !== null);
    return coords.length >= 2 ? [coords] : [];
  }
  if (type === "MultiLineString") {
    const out: Position[][] = [];
    for (const lineRaw of coordinates as unknown as unknown[][]) {
      const coords = lineRaw
        .map(coerceCoord)
        .filter((c): c is Position => c !== null);
      if (coords.length >= 2) out.push(coords);
    }
    return out;
  }
  return [];
};

const cacheKeyFor = (semanticMapId: string): string =>
  `${CACHE_KEY_PREFIX}${PREPARED_TRACK_CACHE_VERSION}:${semanticMapId}`;

const safeReadCache = (semanticMapId: string): PreparedTrack | null => {
  try {
    if (typeof localStorage === "undefined") return null;
    const raw = localStorage.getItem(cacheKeyFor(semanticMapId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PreparedTrack;
    if (
      !parsed ||
      parsed.version !== PREPARED_TRACK_CACHE_VERSION ||
      parsed.semanticMapId !== semanticMapId ||
      !Array.isArray(parsed.lines)
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

const safeWriteCache = (prepared: PreparedTrack): void => {
  try {
    if (typeof localStorage === "undefined") return;
    localStorage.setItem(
      cacheKeyFor(prepared.semanticMapId),
      JSON.stringify(prepared)
    );
  } catch {
    // Quota or serialization issues — silently fall back to in-memory.
  }
};

/**
 * Normalize a SemanticMap track into a PreparedTrack and cache the result
 * in localStorage keyed by semanticMapId + cache version.
 *
 * Returns null if the track has no usable LineString features after
 * normalization (no lines, or all features were degenerate).
 */
export const prepareTrack = (
  track: SemanticMapTrack | null | undefined,
  semanticMapId: string
): PreparedTrack | null => {
  if (!track || !Array.isArray(track.features)) return null;

  const cached = safeReadCache(semanticMapId);
  if (cached && cached.lines.length > 0) return cached;

  const lines: PreparedLine[] = [];
  for (const feature of track.features) {
    const exploded = explodeFeatureToLines(feature);
    for (const coords of exploded) {
      const built = buildLine(coords);
      if (built) lines.push(built);
    }
  }

  if (lines.length === 0) return null;

  const prepared: PreparedTrack = {
    semanticMapId,
    version: PREPARED_TRACK_CACHE_VERSION,
    lines,
  };
  safeWriteCache(prepared);
  return prepared;
};

/**
 * Snap (lat, lng) to the closest point on any line in the prepared track.
 * Returns null if no line is within SNAP_PERP_THRESHOLD_METERS perpendicular.
 */
export const snapPoint = (
  prepared: PreparedTrack,
  lat: number,
  lng: number
): SnapResult | null => {
  if (!isFiniteNumber(lat) || !isFiniteNumber(lng)) return null;
  if (!prepared.lines.length) return null;

  let best: SnapResult | null = null;

  for (let lineIdx = 0; lineIdx < prepared.lines.length; lineIdx++) {
    const line = prepared.lines[lineIdx];
    const lineFeature: Feature<LineString> = {
      type: "Feature",
      geometry: { type: "LineString", coordinates: line.coords },
      properties: {},
    };
    const snapped = nearestPointOnLine(lineFeature, [lng, lat], {
      units: "kilometers",
    });
    const props = snapped.properties;
    const perpDistMeters =
      (props.pointDistance ?? props.dist ?? 0) * KILOMETERS_TO_METERS;
    if (best && perpDistMeters >= best.perpDistMeters) continue;

    const segIdx = props.segmentIndex ?? props.index ?? 0;
    // distAlongLine is the cumulative distance up to segIdx plus the partial
    // segment distance from coords[segIdx] to the snapped point.
    const segStartCum = line.cumulative[segIdx] ?? 0;
    const segEndCum = line.cumulative[segIdx + 1] ?? segStartCum;
    const segLength = segEndCum - segStartCum;
    const lineDistanceMeters =
      (props.lineDistance ?? props.totalDistance ?? props.location ?? 0) *
      KILOMETERS_TO_METERS;
    const distAlongLine =
      lineDistanceMeters > 0 ? lineDistanceMeters : segStartCum;
    const t =
      segLength > 0
        ? Math.max(0, Math.min(1, (distAlongLine - segStartCum) / segLength))
        : 0;

    best = {
      lineIdx,
      segIdx,
      t,
      distAlongLine,
      perpDistMeters,
    };
  }

  if (!best || best.perpDistMeters > SNAP_PERP_THRESHOLD_METERS) return null;
  return best;
};

const haversineMeters = (
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): number => {
  // Earth mean radius in meters (matches @turf default).
  const R = 6371008.8;
  const toRad = (deg: number): number => (deg * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const sinDLat = Math.sin(dLat / 2);
  const sinDLng = Math.sin(dLng / 2);
  const h =
    sinDLat * sinDLat + Math.cos(lat1) * Math.cos(lat2) * sinDLng * sinDLng;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Distance along the track between two snaps.
 *
 * If both snaps are on the same line, this is `|distAlongLine_a - distAlongLine_b|`.
 * Otherwise we have no walkable arc available (the track is a collection of
 * disjoint LineStrings post-explode), so we return Haversine straight-line
 * distance with `sameLine: false` so callers can decide whether to fall back
 * to stop-count progress.
 */
export const arcLengthBetween = (
  prepared: PreparedTrack,
  snapA: SnapResult,
  snapB: SnapResult
): { meters: number; sameLine: boolean } => {
  if (snapA.lineIdx === snapB.lineIdx) {
    return {
      meters: Math.abs(snapA.distAlongLine - snapB.distAlongLine),
      sameLine: true,
    };
  }
  const lineA = prepared.lines[snapA.lineIdx];
  const lineB = prepared.lines[snapB.lineIdx];
  const segIdxA = Math.min(snapA.segIdx, lineA.coords.length - 1);
  const segIdxB = Math.min(snapB.segIdx, lineB.coords.length - 1);
  const a = lineA.coords[segIdxA];
  const b = lineB.coords[segIdxB];
  const meters = haversineMeters(a[1], a[0], b[1], b[0]);
  return { meters, sameLine: false };
};

// Exposed for tests so they can wipe the cache between cases.
export const __clearPreparedTrackCacheForTesting = (): void => {
  try {
    if (typeof localStorage === "undefined") return;
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(CACHE_KEY_PREFIX)) keys.push(k);
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
};

export const __SNAP_PERP_THRESHOLD_METERS = SNAP_PERP_THRESHOLD_METERS;
